import * as React from "react";
import MDEditor from "@uiw/react-md-editor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { AiAssistantSettings } from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export function AiAssistantPromptPage() {
  const qc = useQueryClient();
  const { t } = useTranslation("templates");
  const { data, isLoading, error } = useQuery({
    queryKey: ["ai-settings"],
    queryFn: () => api.get<{ settings: AiAssistantSettings }>("/api/admin/ai-settings"),
  });

  const [value, setValue] = React.useState<string>("");
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (data?.settings && !initialized) {
      setValue(data.settings.systemPrompt || "");
      setInitialized(true);
    }
  }, [data, initialized]);

  const mutation = useMutation({
    mutationFn: (prompt: string) =>
      api.put<{ settings: AiAssistantSettings }>("/api/admin/ai-settings", { systemPrompt: prompt }),
    onSuccess: (res) => {
      toast.success(t("aiAssistant.prompt.saved"));
      qc.setQueryData(["ai-settings"], res);
    },
    onError: (err) => {
      toast.error(
        t("errors.saveFailed", { message: err instanceof Error ? err.message : String(err) })
      );
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/templates" className="hover:underline">{t("list.title")}</Link>
          <span>/</span>
          <span>{t("aiAssistant.breadcrumb")}</span>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/templates/ai-assistant/settings">{t("aiAssistant.modelSettings")}</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("aiAssistant.prompt.title")}</CardTitle>
          <CardDescription>
            {t("aiAssistant.prompt.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-96 w-full" />
          ) : error ? (
            <p className="text-sm text-destructive">
              {t("errors.loadFailed", {
                message: error instanceof Error ? error.message : String(error),
              })}
            </p>
          ) : (
            <div className="space-y-4">
              <div data-color-mode="light">
                <MDEditor value={value} onChange={(v) => setValue(v ?? "")} height={520} />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    if (!value.trim()) {
                      toast.error(t("aiAssistant.prompt.empty"));
                      return;
                    }
                    mutation.mutate(value);
                  }}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? t("actions.saving") : t("actions.save")}
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/templates">{t("actions.backToList")}</Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
